import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { getLastSearchedVehicleKey } from 'domains/catalog/Catalog.store';
import {
  fetchEasyBasketOperationDetailsRequestSaga,
  fetchEasyBasketOperationsRequestSaga,
  getEasyBasketOperation,
  getEasyBasketOperationDetails,
  getEasyBasketOperationsByIds,
  setInitialState,
} from 'domains/easyBasket/EasyBasket.store';
import { getDocumentLanguage } from 'domains/user';
import { FOUND, isLoading } from 'utils';

export const useFetchEasyBasketOperations = (operationsIds: string[]) => {
  const dispatch = useDispatch();
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const operations = useSelector((state: RootState) => getEasyBasketOperationsByIds(state, operationsIds));
  const isAnyLoading = Array.from(operations.values()).some((operation) => isLoading(operation.searchStatus));
  const isAllFound =
    Array.from(operations.values()).length === operationsIds.length &&
    Array.from(operations.values()).every((operation) => operation.searchStatus === FOUND);

  useEffect(() => {
    const shouldFetchWithoutData = vehicleKey && operationsIds && operationsIds.length > 0;
    const shouldFetchWithData = !isAnyLoading && !isAllFound;

    if (shouldFetchWithoutData && shouldFetchWithData) {
      dispatch(fetchEasyBasketOperationsRequestSaga({ vehicleKey, operationsIds }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationsIds, vehicleKey]);
};

export const useFetchEasyBasketOperationDetails = (operationId: string) => {
  const dispatch = useDispatch();
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const operation = useSelector((state: RootState) => getEasyBasketOperation(state, operationId));
  const genericParts = operation?.data?.genericParts ?? [];
  const operationDetails = useSelector((state: RootState) => getEasyBasketOperationDetails(state, operationId));
  const isOperationDetailsLoading = isLoading(operationDetails?.searchStatus);
  const isOperationDetailsFound = operationDetails?.searchStatus === FOUND;

  useEffect(() => {
    const shouldFetchWithoutData = vehicleKey && operationId;
    const shouldFetchWithData = !isOperationDetailsLoading && !isOperationDetailsFound;

    if (shouldFetchWithoutData && shouldFetchWithData) {
      dispatch(
        fetchEasyBasketOperationDetailsRequestSaga({
          vehicleKey,
          genericParts,
          operationsIds: [operationId],
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleKey, operationId, isOperationDetailsFound, isOperationDetailsLoading]);

  return operationDetails?.data;
};

export const useResetEasyBasketState = () => {
  const dispatch = useDispatch();
  const lastSearchedVehicleKey = useSelector(getLastSearchedVehicleKey);
  const documentLanguage = useSelector(getDocumentLanguage);

  useEffect(() => {
    dispatch(setInitialState());
  }, [lastSearchedVehicleKey, documentLanguage, dispatch]);
};
